import { useGetApiChatEvaluationTestGet, useGetApiWorkspaceGet } from "@/API";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Progress,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconCheck,
  IconFileSpreadsheet,
  IconPencil,
  IconPrinter,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import {
  buildEvaluationReportSummary,
  EVALUATION_GLOSSARY,
  formatDurationSec,
  generateEvaluationCsv,
  MetricAggregationType,
} from "./evaluationReportUtils";
import { BlobSaveToFile } from "@/Helpers/SaveToFile";
import { MarkdownMessage } from "@/Components/MarkdownMessage";
import "./ChatEvaluationReportPage.css";

export function ChatEvaluationReportPage() {
  const { workspaceId, chatEvaluationTestSetId, chatEvaluationTestId } = useParams({
    from: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId/test/$chatEvaluationTestId/report",
  });

  const search = useSearch({
    from: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId/test/$chatEvaluationTestId/report",
  }) as { title?: string; customerName?: string; mode?: MetricAggregationType; markdown?: string };

  const navigate = useNavigate({
    from: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId/test/$chatEvaluationTestId/report",
  });

  const [mode, setMode] = useState<MetricAggregationType>(search?.mode || "avg");
  const [renderMarkdown, setRenderMarkdown] = useState<boolean>(search?.markdown !== "false");

  const [customTitle, setCustomTitle] = useState<string | null>(search?.title?.trim() || null);
  const [customCustomer, setCustomCustomer] = useState<string | null>(search?.customerName?.trim() || null);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState("");

  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [tempCustomer, setTempCustomer] = useState("");

  const workspace = useGetApiWorkspaceGet({ id: workspaceId });
  const testResult = useGetApiChatEvaluationTestGet({ workspaceId, id: chatEvaluationTestId });

  const rawResults = testResult.data?.data.results ?? [];
  const summary = useMemo(
    () => buildEvaluationReportSummary(rawResults, mode),
    [rawResults, mode]
  );

  const defaultTitle = testResult.data?.data.name
    ? `${testResult.data.data.name} - AI 智慧問答成效評估報告`
    : "AI 智慧問答成效評估報告";
  const reportTitle = (customTitle !== null ? customTitle : search?.title?.trim()) || defaultTitle;

  const defaultCustomer = workspace.data?.data.name || "企業客戶";
  const customerName = (customCustomer !== null ? customCustomer : search?.customerName?.trim()) || defaultCustomer;

  useEffect(() => {
    if (reportTitle) {
      document.title = reportTitle;
    }
  }, [reportTitle]);

  const handleSaveTitle = () => {
    const trimmed = tempTitle.trim();
    setCustomTitle(trimmed || null);
    setIsEditingTitle(false);
    navigate({
      search: (prev: any) => ({
        ...prev,
        title: trimmed || undefined,
      }),
      replace: true,
    });
  };

  const handleSaveCustomer = () => {
    const trimmed = tempCustomer.trim();
    setCustomCustomer(trimmed || null);
    setIsEditingCustomer(false);
    navigate({
      search: (prev: any) => ({
        ...prev,
        customerName: trimmed || undefined,
      }),
      replace: true,
    });
  };

  const ts = testResult.data?.data.createdTimestamp;
  const evalDate = ts
    ? dayjs.unix(ts).format("YYYY 年 MM 月 DD 日")
    : dayjs().format("YYYY 年 MM 月 DD 日");

  const reportId = `EVAL-${(chatEvaluationTestId || "").slice(-8).toUpperCase()}`;

  const handleDownloadCsv = () => {
    const csvContent = generateEvaluationCsv(rawResults, mode);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const safeTitle = (reportTitle.trim() || "評估結果").replace(/[\\/:*?"<>|]/g, "_");
    const fileName = `${safeTitle}_原始數據.csv`;
    BlobSaveToFile(blob, fileName);
  };

  if (testResult.isLoading) {
    return (
      <Box p="xl" style={{ textAlign: "center", minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Stack align="center" gap="sm">
          <Loader size="lg" color="blue" />
          <Text c="dimmed">正在載入評估報告數據...</Text>
        </Stack>
      </Box>
    );
  }

  if (testResult.isError) {
    return (
      <Box p="xl" style={{ textAlign: "center", minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Stack align="center" gap="md">
          <Text c="red" fw={600} size="lg">載入評估報告時發生錯誤</Text>
          <Text c="dimmed" size="sm">請確認報告是否存在或稍後再試。</Text>
          <Button
            variant="light"
            color="blue"
            size="xs"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => {
              if (window.history.length > 1) {
                window.history.back();
              } else {
                window.close();
              }
            }}
          >
            返回
          </Button>
        </Stack>
      </Box>
    );
  }

  const formatPercent = (score: number | null | undefined) =>
    score != null && !Number.isNaN(score) ? `${(score * 100).toFixed(1)}%` : "無評分";

  return (
    <Box bg="#f8f9fa" className="report-wrapper" style={{ minHeight: "100vh" }}>
      {/* Screen-only Sticky Top Action Bar */}
      <Box
        className="no-print"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #e9ecef",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        }}
        py="xs"
        px="md"
      >
        <Container size="lg">
          <Group justify="space-between" align="center" wrap="wrap">
            <Group gap="sm" align="center">
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => {
                  if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    window.close();
                  }
                }}
              >
                返回
              </Button>
              <Divider orientation="vertical" />
              <Group gap={6} align="center">
                <Text size="xs" fw={600} c="dimmed">
                  多輪指標計算模式：
                </Text>
                <SegmentedControl
                  size="xs"
                  color="blue"
                  value={mode}
                  onChange={(val) => setMode(val as MetricAggregationType)}
                  data={[
                    { label: "平均值", value: "avg" },
                    { label: "最高分", value: "max" },
                    { label: "最低分", value: "min" },
                  ]}
                />
              </Group>
              <Divider orientation="vertical" />
              <Switch
                size="xs"
                color="blue"
                label="AI 回覆 Markdown"
                checked={renderMarkdown}
                onChange={(e) => setRenderMarkdown(e.currentTarget.checked)}
              />
            </Group>

            <Group gap="xs">
              <Button
                variant="light"
                color="blue"
                size="xs"
                leftSection={<IconFileSpreadsheet size={16} />}
                onClick={handleDownloadCsv}
              >
                下載 Excel (CSV)
              </Button>
              <Button
                variant="filled"
                color="blue"
                size="xs"
                leftSection={<IconPrinter size={16} />}
                onClick={() => window.print()}
              >
                列印 / 另存為 PDF
              </Button>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => window.close()}
                title="關閉報告"
              >
                <IconX size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Container>
      </Box>

      {/* Main Report Document Container */}
      <Container size="lg" py="xl" className="print-container">
        <Stack gap="xl">
          {/* Header Card */}
          <Paper
            radius="md"
            p="xl"
            withBorder
            bg="white"
            className="report-paper report-header-paper report-section"
            style={{
              borderTop: "6px solid #1c7ed6",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            }}
          >
            <Stack gap="md">
              <Group justify="space-between" align="flex-start" wrap="wrap">
                <Box style={{ flex: 1, minWidth: 280 }}>
                  <Group gap="xs" mb={4}>
                    <Badge color="blue" variant="filled" size="sm" radius="xs">
                      AI 專業成效驗收報告
                    </Badge>
                    <Text size="xs" c="dimmed">
                      機密文件 / 專屬交付
                    </Text>
                  </Group>
                  {isEditingTitle ? (
                    <>
                      <Title order={2} c="#183247" fw={700} style={{ letterSpacing: "-0.5px" }} className="print-only">
                        {reportTitle}
                      </Title>
                      <Group gap="xs" align="center" mt={4} className="no-print" style={{ maxWidth: 540 }}>
                        <TextInput
                          size="sm"
                          value={tempTitle}
                          onChange={(e) => setTempTitle(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveTitle();
                            if (e.key === "Escape") setIsEditingTitle(false);
                          }}
                          placeholder="輸入報告標題（留空還原預設）"
                          autoFocus
                          style={{ flex: 1 }}
                        />
                        <ActionIcon color="blue" variant="filled" size="md" onClick={handleSaveTitle} title="儲存標題">
                          <IconCheck size={16} />
                        </ActionIcon>
                        <ActionIcon color="gray" variant="subtle" size="md" onClick={() => setIsEditingTitle(false)} title="取消">
                          <IconX size={16} />
                        </ActionIcon>
                      </Group>
                    </>
                  ) : (
                    <Box
                      onClick={() => {
                        setTempTitle(reportTitle);
                        setIsEditingTitle(true);
                      }}
                      className="editable-heading"
                      style={{
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        marginLeft: "-6px",
                      }}
                      title="點擊修改報告標題"
                    >
                      <Title order={2} c="#183247" fw={700} style={{ letterSpacing: "-0.5px" }}>
                        {reportTitle}
                      </Title>
                      <IconPencil size={18} className="edit-icon no-print" style={{ color: "#868e96", flexShrink: 0 }} />
                    </Box>
                  )}
                </Box>
                <Box ta={{ base: "left", sm: "right" }}>
                  <Text size="xs" c="dimmed" fw={600}>
                    報告編號
                  </Text>
                  <Text size="sm" fw={700} style={{ fontFamily: "monospace" }}>
                    {reportId}
                  </Text>
                </Box>
              </Group>

              <Divider />

              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md" className="print-cols-4">
                <Box>
                  <Text size="xs" c="dimmed">
                    受測客戶 / 專案
                  </Text>
                  {isEditingCustomer ? (
                    <>
                      <Text size="sm" fw={600} c="#1c7ed6" className="print-only">
                        {customerName}
                      </Text>
                      <Group gap={4} align="center" mt={2} className="no-print" style={{ maxWidth: 220 }}>
                        <TextInput
                          size="xs"
                          value={tempCustomer}
                          onChange={(e) => setTempCustomer(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveCustomer();
                            if (e.key === "Escape") setIsEditingCustomer(false);
                          }}
                          placeholder="輸入客戶名稱（留空還原）"
                          autoFocus
                          style={{ flex: 1 }}
                        />
                        <ActionIcon color="blue" variant="filled" size="xs" onClick={handleSaveCustomer} title="儲存客戶">
                          <IconCheck size={12} />
                        </ActionIcon>
                        <ActionIcon color="gray" variant="subtle" size="xs" onClick={() => setIsEditingCustomer(false)} title="取消">
                          <IconX size={12} />
                        </ActionIcon>
                      </Group>
                    </>
                  ) : (
                    <Box
                      onClick={() => {
                        setTempCustomer(customerName);
                        setIsEditingCustomer(true);
                      }}
                      className="editable-heading"
                      style={{
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        borderRadius: "4px",
                        padding: "1px 4px",
                        marginLeft: "-4px",
                      }}
                      title="點擊修改受測客戶"
                    >
                      <Text size="sm" fw={600} c="#1c7ed6">
                        {customerName}
                      </Text>
                      <IconPencil size={14} className="edit-icon no-print" style={{ color: "#868e96", flexShrink: 0 }} />
                    </Box>
                  )}
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    評估執行日期
                  </Text>
                  <Text size="sm" fw={600}>
                    {evalDate}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    問答測試樣本數
                  </Text>
                  <Text size="sm" fw={600}>
                    {summary.totalCount} 筆驗證情境
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    總體成效水準
                  </Text>
                  <Badge color={summary.overallTier.color} variant="light" size="md">
                    {summary.overallTier.shortLabel}
                  </Badge>
                </Box>
              </SimpleGrid>
            </Stack>
          </Paper>

          {/* Chapter 1: Executive Summary */}
          <Box className="report-section">
            <Group gap="xs" mb="sm" align="center">
              <Box w={4} h={22} bg="#1c7ed6" style={{ borderRadius: 2 }} />
              <Title order={3} size="h4" c="#183247">
                第一章、成效指標總覽 (Executive Summary)
              </Title>
            </Group>

            {/* Core Metrics 4 Cards */}
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md" mb="md" className="print-cols-4">
              {/* Answer Accuracy */}
              <Paper p="md" radius="md" withBorder bg="white" className="report-paper metric-card">
                <Stack justify="space-between" h="100%" gap="xs">
                  <Box>
                    <Group justify="space-between" align="center" mb={4}>
                      <Text size="sm" fw={700} c="#183247">
                        答案準確度 (Accuracy)
                      </Text>
                      <Badge color={summary.metrics.accuracy.tier.color} variant="light" size="sm">
                        {summary.metrics.accuracy.tier.shortLabel}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      衡量回覆與標準答案的契合與正確程度
                    </Text>
                  </Box>

                  <Group align="baseline" gap="xs" my="xs">
                    <Text size="2.2rem" fw={800} c="#1c7ed6" style={{ lineHeight: 1 }}>
                      {formatPercent(summary.metrics.accuracy.averageScore)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      / 100%
                    </Text>
                  </Group>

                  <Progress
                    value={(summary.metrics.accuracy.averageScore ?? 0) * 100}
                    color={summary.metrics.accuracy.tier.color}
                    size="sm"
                    radius="xl"
                  />
                </Stack>
              </Paper>

              {/* Context Relevance */}
              <Paper p="md" radius="md" withBorder bg="white" className="report-paper metric-card">
                <Stack justify="space-between" h="100%" gap="xs">
                  <Box>
                    <Group justify="space-between" align="center" mb={4}>
                      <Text size="sm" fw={700} c="#183247">
                        內容相關性 (Relevance)
                      </Text>
                      <Badge color={summary.metrics.contextRelevance.tier.color} variant="light" size="sm">
                        {summary.metrics.contextRelevance.tier.shortLabel}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      評估知識庫檢索內容與提問的切題程度
                    </Text>
                  </Box>

                  <Group align="baseline" gap="xs" my="xs">
                    <Text size="2.2rem" fw={800} c="#0ca678" style={{ lineHeight: 1 }}>
                      {formatPercent(summary.metrics.contextRelevance.averageScore)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      / 100%
                    </Text>
                  </Group>

                  <Progress
                    value={(summary.metrics.contextRelevance.averageScore ?? 0) * 100}
                    color={summary.metrics.contextRelevance.tier.color}
                    size="sm"
                    radius="xl"
                  />
                </Stack>
              </Paper>

              {/* Response Groundedness */}
              <Paper p="md" radius="md" withBorder bg="white" className="report-paper metric-card">
                <Stack justify="space-between" h="100%" gap="xs">
                  <Box>
                    <Group justify="space-between" align="center" mb={4}>
                      <Text size="sm" fw={700} c="#183247">
                        回應根據性 (Groundedness)
                      </Text>
                      <Badge color={summary.metrics.groundedness.tier.color} variant="light" size="sm">
                        {summary.metrics.groundedness.tier.shortLabel}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      檢驗回覆是否完全源自檢索知識、避免幻覺
                    </Text>
                  </Box>

                  <Group align="baseline" gap="xs" my="xs">
                    <Text size="2.2rem" fw={800} c="#1098ad" style={{ lineHeight: 1 }}>
                      {formatPercent(summary.metrics.groundedness.averageScore)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      / 100%
                    </Text>
                  </Group>

                  <Progress
                    value={(summary.metrics.groundedness.averageScore ?? 0) * 100}
                    color={summary.metrics.groundedness.tier.color}
                    size="sm"
                    radius="xl"
                  />
                </Stack>
              </Paper>

              {/* Average Response Time */}
              <Paper p="md" radius="md" withBorder bg="white" className="report-paper metric-card">
                <Stack justify="space-between" h="100%" gap="xs">
                  <Box>
                    <Group justify="space-between" align="center" mb={4}>
                      <Text size="sm" fw={700} c="#183247">
                        平均回應時間 (Response Time)
                      </Text>
                      <Badge color="blue" variant="light" size="sm">
                        系統效能
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      衡量模型生成對話之響應耗時
                    </Text>
                  </Box>

                  <Group align="baseline" gap="xs" my="xs">
                    <Text size="2.2rem" fw={800} c="#1c7ed6" style={{ lineHeight: 1 }}>
                      {summary.durationSummary.chatDurationAvg != null
                        ? (summary.durationSummary.chatDurationAvg / 1000).toFixed(2)
                        : "-"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      / 秒 (s)
                    </Text>
                  </Group>

                  <Text size="xs" c="dimmed">
                    {summary.durationSummary.chatDurationMin != null && summary.durationSummary.chatDurationMax != null
                      ? `最快 ${(summary.durationSummary.chatDurationMin / 1000).toFixed(2)} s ~ 最慢 ${(summary.durationSummary.chatDurationMax / 1000).toFixed(2)} s`
                      : "無極值區間資料"}
                  </Text>
                </Stack>
              </Paper>
            </SimpleGrid>

            {/* Distribution and Token Stats Grid */}
            <Grid gap="md" className="print-split-7-5">
              {/* Distribution Card */}
              <Grid.Col span={{ base: 12, md: 7 }} className="print-col-7">
                <Paper p="md" radius="md" withBorder bg="white" className="report-paper" h="100%">
                  <Text size="sm" fw={700} c="#183247" mb="xs">
                    評估成效分佈統計 (Distribution)
                  </Text>
                  <Text size="xs" c="dimmed" mb="md">
                    呈現測試樣本在各成效梯隊的分佈比例，體現系統整體的回答穩健度。
                  </Text>

                  <Stack gap="sm">
                    {summary.tierDistributions.map((dist) => (
                      <Box key={dist.tier.id}>
                        <Group justify="space-between" mb={2}>
                          <Group gap={6}>
                            <Badge size="xs" color={dist.tier.color} variant="filled">
                              {dist.tier.shortLabel}
                            </Badge>
                            <Text size="xs" fw={500}>
                              {dist.tier.label}
                            </Text>
                          </Group>
                          <Text size="xs" fw={700} c="dimmed">
                            {dist.count} 筆 ({dist.percentage}%)
                          </Text>
                        </Group>
                        <Progress value={dist.percentage} color={dist.tier.color} size="sm" radius="xl" />
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid.Col>

              {/* Token & System Resource Card */}
              <Grid.Col span={{ base: 12, md: 5 }} className="print-col-5">
                <Paper p="md" radius="md" withBorder bg="white" className="report-paper" h="100%">
                  <Text size="sm" fw={700} c="#183247" mb="xs">
                    運算資源與模型概況 (Resource & LLM)
                  </Text>
                  <Text size="xs" c="dimmed" mb="md">
                    記錄對話生成之運算消耗與後台支援之語言模型。
                  </Text>

                  <Stack gap="xs">
                    <Group justify="space-between" py={4} style={{ borderBottom: "1px dashed #e9ecef" }}>
                      <Text size="xs" c="dimmed">
                        對話生成 Token (Chat)
                      </Text>
                      <Text size="xs" fw={600}>
                        {summary.tokenUsage.chatInputTokens.toLocaleString()} In / {summary.tokenUsage.chatOutputTokens.toLocaleString()} Out
                      </Text>
                    </Group>

                    <Tooltip
                      label={`[對話] ${summary.tokenUsage.chatTotalTokens.toLocaleString()} Tokens (${summary.tokenUsage.chatInputTokens.toLocaleString()} In / ${summary.tokenUsage.chatOutputTokens.toLocaleString()} Out)\n[評估] ${summary.tokenUsage.evaluationTotalTokens.toLocaleString()} Tokens (${summary.tokenUsage.evaluationInputTokens.toLocaleString()} In / ${summary.tokenUsage.evaluationOutputTokens.toLocaleString()} Out)`}
                      multiline
                      style={{ whiteSpace: "pre-line" }}
                    >
                      <Group justify="space-between" py={4} style={{ borderBottom: "1px dashed #e9ecef", cursor: "help" }}>
                        <Text size="xs" c="dimmed">
                          對話 Token 總計
                        </Text>
                        <Text size="xs" fw={700} c="#1c7ed6">
                          {summary.tokenUsage.chatTotalTokens.toLocaleString()} Tokens
                        </Text>
                      </Group>
                    </Tooltip>

                    <Box mt="xs">
                      <Tooltip
                        label={
                          summary.tokenUsage.evaluationModels && summary.tokenUsage.evaluationModels.length > 0
                            ? `評估評判模型: ${summary.tokenUsage.evaluationModels.join(", ")}`
                            : ""
                        }
                        disabled={!summary.tokenUsage.evaluationModels || summary.tokenUsage.evaluationModels.length === 0}
                        style={{ whiteSpace: "pre-line" }}
                      >
                        <Text
                          size="xs"
                          c="dimmed"
                          mb={4}
                          style={{
                            display: "inline-block",
                            cursor: summary.tokenUsage.evaluationModels?.length ? "help" : "default",
                          }}
                        >
                          對話生成模型
                        </Text>
                      </Tooltip>
                      <Group gap={4}>
                        {summary.tokenUsage.models.length > 0 ? (
                          summary.tokenUsage.models.map((m) => (
                            <Badge key={m} size="xs" variant="outline" color="blue">
                              {m}
                            </Badge>
                          ))
                        ) : (
                          <Text size="xs" c="dimmed">
                            標準企業語言模型
                          </Text>
                        )}
                      </Group>
                    </Box>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Box>

          {/* Chapter 2: Methodology & Glossary */}
          <Box className="report-section print-page-break-before">
            <Group gap="xs" mb="sm" align="center" className="report-section-title">
              <Box w={4} h={22} bg="#099268" style={{ borderRadius: 2 }} />
              <Title order={3} size="h4" c="#183247">
                第二章、評估指標與名詞解釋 (Glossary & Methodology)
              </Title>
            </Group>

            <Paper p="md" radius="md" withBorder bg="white" className="report-paper">
              <Text size="xs" c="dimmed" mb="md">
                本系統採用業界權威之 RAG 檢索與生成雙向評量體系，嚴謹量化智慧客服之實用性與安全性。以下為核心指標之專有名詞定義與商務價值說明：
              </Text>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" className="print-cols-2">
                {EVALUATION_GLOSSARY.map((item) => (
                  <Card key={item.id} p="sm" radius="sm" withBorder bg="#fdfdfe" className="glossary-card">
                    <Group gap="xs" mb={4} align="center">
                      <IconSparkles size={16} color="#1c7ed6" />
                      <Text size="sm" fw={700} c="#183247">
                        {item.nameZh}
                      </Text>
                      <Text size="xs" c="dimmed">
                        ({item.nameEn})
                      </Text>
                    </Group>
                    <Text size="xs" c="#495057" mb="xs" style={{ lineHeight: 1.6 }}>
                      {item.definition}
                    </Text>
                    <Box p={6} bg="#e7f5ff" style={{ borderRadius: 4 }}>
                      <Text size="xs" c="#1971c2" fw={500}>
                        💡 商務價值：{item.businessValue}
                      </Text>
                    </Box>
                  </Card>
                ))}
              </SimpleGrid>
            </Paper>
          </Box>

          {/* Chapter 3: Execution Records Appendix */}
          <Box className="report-section print-page-break-before">
            <Group justify="space-between" align="center" mb="sm" wrap="wrap" gap="xs" className="report-section-title">
              <Group gap="xs" align="center">
                <Box w={4} h={22} bg="#1098ad" style={{ borderRadius: 2 }} />
                <Title order={3} size="h4" c="#183247">
                  第三章、完整測試紀錄附錄 (Test Execution Records Appendix)
                </Title>
              </Group>
              <Text size="xs" c="dimmed">
                共 {summary.items.length} 筆測試案例
              </Text>
            </Group>

            <Stack gap="md">
              {summary.items.map((item) => (
                <Paper
                  key={item.index}
                  p="md"
                  radius="md"
                  withBorder
                  bg="white"
                  className="report-paper record-card"
                >
                    {/* Card Header */}
                    <Group justify="space-between" align="center" mb="xs" wrap="wrap" gap="xs">
                      <Group gap="xs" align="center">
                        <Badge color="dark" variant="filled" size="sm" radius="xs">
                          測試案例 #{item.index}
                        </Badge>
                        <Badge color={item.tier.color} variant="light" size="sm">
                          {item.tier.shortLabel}
                        </Badge>
                        <Text size="xs" fw={700} c="#1c7ed6">
                          綜合評分：{formatPercent(item.averageScore)}
                        </Text>
                      </Group>

                      <Group gap="md" align="center" wrap="wrap">
                        <Group gap="xs">
                          <Text size="11px" c="dimmed">
                            準確度：<b style={{ color: "#183247" }}>{formatPercent(item.accuracy)}</b>
                          </Text>
                          <Text size="11px" c="dimmed">
                            相關性：<b style={{ color: "#183247" }}>{formatPercent(item.contextRelevance)}</b>
                          </Text>
                          <Text size="11px" c="dimmed">
                            根據性：<b style={{ color: "#183247" }}>{formatPercent(item.groundedness)}</b>
                          </Text>
                        </Group>
                        <Divider orientation="vertical" />
                        <Tooltip
                          label={`[對話] ${item.chatInputTokens} In / ${item.chatOutputTokens} Out\n[評估] ${item.evaluationInputTokens} In / ${item.evaluationOutputTokens} Out`}
                          multiline
                          style={{ whiteSpace: "pre-line" }}
                        >
                          <Text size="11px" c="dimmed">
                            Token 使用量：<b style={{ color: "#183247" }}>{item.chatInputTokens} In / {item.chatOutputTokens} Out</b>
                          </Text>
                        </Tooltip>
                        <Divider orientation="vertical" />
                        <Tooltip
                          label={`[對話] ${formatDurationSec(item.chatDurationMs)}\n[評估] ${formatDurationSec(item.evaluationDurationMs)}`}
                          multiline
                          style={{ whiteSpace: "pre-line" }}
                        >
                          <Text size="11px" c="dimmed">
                            回應時間：<b style={{ color: "#183247" }}>{formatDurationSec(item.chatDurationMs)}</b>
                          </Text>
                        </Tooltip>
                      </Group>
                    </Group>

                    <Divider mb="xs" />

                    {/* Content Sections with clean line breaks and adequate breathing space */}
                    <Stack gap="xs">
                      {/* User Inquiry */}
                      <Box>
                        <Text size="xs" fw={700} c="#183247" mb={4}>
                          💬 使用者提問 (User Inquiry)：
                        </Text>
                        <Paper p="xs" bg="#f8f9fa" radius="xs" withBorder>
                          <Text size="xs" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>
                            {item.userInput}
                          </Text>
                        </Paper>
                      </Box>

                      {/* Reference Answer */}
                      <Box>
                        <Text size="xs" fw={700} c="#495057" mb={4}>
                          📘 標準參考回答 (Reference Answer)：
                        </Text>
                        <Paper p="xs" bg="#f1f3f5" radius="xs" withBorder>
                          <Text size="xs" c="#495057" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>
                            {item.reference || "無標準參考答案"}
                          </Text>
                        </Paper>
                      </Box>

                      {/* AI Generated Response */}
                      <Box>
                        <Text size="xs" fw={700} c="#1c7ed6" mb={4}>
                          🤖 AI 實際生成回覆 (AI Generated Response)：
                        </Text>
                        <Paper p="xs" bg="#f8faff" radius="xs" style={{ border: "1px solid #d0ebff" }}>
                          {renderMarkdown ? (
                            <Box className="markdown-content" style={{ fontSize: "12px", lineHeight: 1.6, wordBreak: "break-word" }}>
                              <MarkdownMessage>{item.aiResponse}</MarkdownMessage>
                            </Box>
                          ) : (
                            <Text size="xs" c="#183247" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>
                              {item.aiResponse}
                            </Text>
                          )}
                        </Paper>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
        </Stack>
      </Container>
    </Box>
  );
}
